import React, { useState, useEffect } from 'react';
import { Edit3, Trash2, X, Clock, Calendar, ChevronDown, Trash, CircleX, SquareChartGantt } from 'lucide-react';
import './EventPopup.scss';
import { ArrowRepeatAllIcon } from '../../../modules/default-svg';
import moment from 'moment';

const EventPopup = ({ event, position, onClose, onEdit, onDelete, onCancel }) => {
  const [showDropdown, setShowDropdown] = useState("");
  const settings = JSON.parse(localStorage.getItem('settings') || '{}');
  const dateTimeSettings = {
    dateFormat: 'en-US',
    timeFormat: settings?.regionalSettings?.timeFormat ? (settings?.regionalSettings?.timeFormat.toLowerCase().includes("a") ? '12h' : '24h')  :'12h',
    locale: 'en-US'
  };

  if (!event || !position) return null;

  const viewOnly = event._def.extendedProps.isEditingDisabled;
  const isSameDay = moment(event.start).isSame(moment(event.end), 'day');
  
  const formatTime = (date) => {
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: dateTimeSettings.timeFormat === '12h'
    };
    
    return new Intl.DateTimeFormat(dateTimeSettings.locale, options).format(date);
  };

  const formatDate = (date) => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    return new Intl.DateTimeFormat(dateTimeSettings.locale, options).format(date);
  };

  const getDuration = () => {
    if (!event.end) return '';
    const duration = (event.end - event.start) / (1000 * 60);
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  const getEventTypeColor = () => {
    //const type = event.extendedProps?.type || 'meeting';
    const type = 'meeting';
    const colors = {
      meeting: 'transparent',
    };
    return colors[type] || colors.meeting;
  };

  const isRecurring = event.extendedProps?.isOccurrence || false;
  const isCanceled = event.extendedProps?.isCanceled || false;


  const handleAction = (type, actionType) =>{
    switch(type){
        case "edit":
            onEdit(event, actionType);
        break;
        case "delete":
            onDelete(event, actionType);
        break;
        case "cancel":
            onCancel(event, actionType);
        break;    
    }
    onClose();
  }
  
  // Calculate popup position to stay within viewport
  const popupStyle = {
    left: Math.min(position.x, window.innerWidth - 320),
    top: Math.min(position.y, window.innerHeight - 400)
  };

  return (
    <div className="event-popup-wrapper">
      <div 
        className="popup-backdrop"
        onClick={onClose}
      />
      <div 
        className="popup-container"
        style={popupStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header">
          <div className="header-content">
            <div className="event-type-badge">
              <div 
                className="type-indicator"
                style={{ backgroundColor: getEventTypeColor() }}
              />
              <span className="type-label">
                {/* {event.extendedProps?.type || 'Meeting'} */}
              </span>
            </div>
            <div className="event-title">
                {event.title}
            </div>
          </div>
          <button
            onClick={onClose}
            className="close-button"
          >
            <X />
          </button>
        </div>

        {/* Event Details */}
        <div className="popup-details">
          <div className="detail-item">
            <div className="detail-icon">
              <Calendar />
            </div>
            <span className="detail-content">
              {isSameDay ? formatDate(event.start) : `${formatDate(event.start)} to ${formatDate(event.end) }`}
            </span>
            {isRecurring && <ArrowRepeatAllIcon/>} 
            {event._def.extendedProps.isAllDay && isSameDay && <span className="all-day-pill">All day</span>} 
          </div>
          
          {
            !event._def.extendedProps.isAllDay &&
            (
              <div className="detail-item">
                <div className="detail-icon">
                  <Clock />
                </div>
                <div className="detail-content">
                      <span>{formatTime(event.start)}</span>
                      {event.end && (
                        <>
                          <span className="time-separator">–</span>
                          <span>{formatTime(event.end)}</span>
                          <span className="duration">
                            ({getDuration()})
                          </span>
                        </>
                      )}
                </div>
            </div>
            )
          }


          {event.extendedProps?.description && (
            <div className="event-description">
              {event.extendedProps.description}
            </div>
          )}
        </div>

        <div className="popup-actions">
          <div className="actions-container">
            <div className="actions-dropdown">
                {
                    viewOnly ?
                    <button 
                        onClick={() => handleAction('edit',0)}
                        className="action-button edit"
                    >
                        <SquareChartGantt />
                        Details
                    </button>
                    :
                    <button 
                        onClick={() => isRecurring ? setShowDropdown(showDropdown == "edit" ? "" :"edit") : handleAction('edit',0)}
                        className="action-button edit"
                    >
                        <Edit3 />
                        Edit
                        {isRecurring &&<ChevronDown className="chevron" />}
                    </button>
                }

              {showDropdown == "edit" && (
                <div className="event-dropdown-menu">
                  <button
                    onClick={() => handleAction('edit',0)}
                    className="event-dropdown-item"
                  >
                    This event
                  </button>
                  {isRecurring && (
                    <>
                      <button
                        onClick={() => handleAction('edit',1)}
                        className="event-dropdown-item"
                      >
                        This and all following events
                      </button>
                      <button
                        onClick={() => handleAction('edit',2)}
                        className="event-dropdown-item"
                      >
                        All events in the series
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {!viewOnly && (
             <>
             {
              !isCanceled && 
              (
                <div className="actions-dropdown">
                  <button 
                    onClick={() => isRecurring ? setShowDropdown(showDropdown == "cancel" ? "" :"cancel") : handleAction('cancel',0)}
                    className="action-button cancel"
                  >
                    <CircleX />
                    Cancel
                    {isRecurring &&<ChevronDown className="chevron" />}
                  </button>
              
                  {showDropdown == "cancel" && (
                    <div className="event-dropdown-menu">
                      <button
                        onClick={() => handleAction('cancel',0)}
                        className="event-dropdown-item"
                      >
                        This event
                      </button>
                      {isRecurring && (
                        <>
                          <button
                            onClick={() => handleAction('cancel',1)}
                            className="event-dropdown-item"
                          >
                            This and all following events
                          </button>
                          <button
                            onClick={() => handleAction('cancel',2)}
                            className="event-dropdown-item"
                          >
                            All events in the series
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )
             }
            <div className="actions-dropdown">
              <button 
                onClick={() => isRecurring ? setShowDropdown(showDropdown == "delete" ? "" :"delete") : handleAction('delete',0)}
                className="action-button delete"
              >
                <Trash2 />
                Delete
                {isRecurring &&<ChevronDown className="chevron" />}
              </button>
              
              {showDropdown == "delete" && (
                <div className="event-dropdown-menu">
                  <button
                    onClick={() => handleAction('delete',0)}
                    className="event-dropdown-item"
                  >
                    This event
                  </button>
                  {isRecurring && (
                    <>
                      <button
                        onClick={() => handleAction('delete',1)}
                        className="event-dropdown-item"
                      >
                        This and all following events
                      </button>
                      <button
                        onClick={() => handleAction('delete',2)}
                        className="event-dropdown-item"
                      >
                        All events in the series
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
             </>
            )}
            
          </div>


        </div>
      </div>
    </div>
  );
};

export default EventPopup;